"use client"

import type React from "react"
import { createContext, useContext, useState, useCallback } from "react"
import type { ActiveUnits } from "../types/table"

const defaultUnits: ActiveUnits = {
  length: "m",
  mass: "kg",
  volume: "m3",
  area: "m2",
  temperature: "C",
  power: "kW",
  voltage: "kV",
}

interface UnitsContextType {
  activeUnits: ActiveUnits
  setActiveUnits: (units: ActiveUnits) => void
  subscribers: Set<() => void>
  subscribe: (callback: () => void) => () => void
}

const UnitsContext = createContext<UnitsContextType | undefined>(undefined)

export function UnitsProvider({ children }: { children: React.ReactNode }) {
  const [activeUnits, setActiveUnits] = useState<ActiveUnits>(defaultUnits)
  const [subscribers] = useState(new Set<() => void>())

  const subscribe = useCallback(
    (callback: () => void) => {
      subscribers.add(callback)
      return () => {
        subscribers.delete(callback)
      }
    },
    [subscribers],
  )

  const handleSetActiveUnits = useCallback(
    (newUnits: ActiveUnits) => {
      setActiveUnits(newUnits)
      // Notify all subscribers
      subscribers.forEach((callback) => callback())
    },
    [subscribers],
  )

  return (
    <UnitsContext.Provider
      value={{
        activeUnits,
        setActiveUnits: handleSetActiveUnits,
        subscribers,
        subscribe,
      }}
    >
      {children}
    </UnitsContext.Provider>
  )
}

export function useUnits() {
  const context = useContext(UnitsContext)
  if (!context) {
    throw new Error("useUnits must be used within a UnitsProvider")
  }
  return context
}

